import { Injectable } from '@angular/core';
import { Action, State, type StateContext } from '@ngxs/store';
import { PipelineState } from '../../lib/table-pipeline/contracts';
import * as Act from './table-pipeline.actions';
import {
  tablePipelineStateDefaults,
  TablePipelineStateModel
} from './table-pipeline.state.model';

const without = <K extends string>(
  obj: Readonly<Record<K, PipelineState>>,
  key: K
): Record<K, PipelineState> => {
  const next = { ...obj };
  delete next[key];

  return next;
};

const merge = (
  prev: PipelineState | undefined,
  patch: Partial<PipelineState>
): PipelineState => {
  const next: PipelineState = { ...prev, ...patch };
  if (next.filter === undefined) delete (next as { filter?: unknown }).filter;
  if (next.sort === undefined) delete (next as { sort?: unknown }).sort;
  if (next.view === undefined) delete (next as { view?: unknown }).view;

  return next;
};

@State<TablePipelineStateModel>({
  name: 'tablePipeline',
  defaults: tablePipelineStateDefaults
})
@Injectable()
export class TablePipelineState {
  @Action(Act.SetFilter)
  public setFilter(
    ctx: StateContext<TablePipelineStateModel>,
    { tableId, filter }: Act.SetFilter
  ): void {
    const { byTable } = ctx.getState();
    ctx.patchState({
      byTable: { ...byTable, [tableId]: merge(byTable[tableId], { filter }) }
    });
  }

  @Action(Act.SetSort)
  public setSort(
    ctx: StateContext<TablePipelineStateModel>,
    { tableId, sort }: Act.SetSort
  ): void {
    const { byTable } = ctx.getState();
    ctx.patchState({
      byTable: { ...byTable, [tableId]: merge(byTable[tableId], { sort }) }
    });
  }

  @Action(Act.SetView)
  public setView(
    ctx: StateContext<TablePipelineStateModel>,
    { tableId, view }: Act.SetView
  ): void {
    const { byTable } = ctx.getState();
    ctx.patchState({
      byTable: { ...byTable, [tableId]: merge(byTable[tableId], { view }) }
    });
  }

  @Action(Act.Reset)
  public reset(
    ctx: StateContext<TablePipelineStateModel>,
    { tableId }: Act.Reset
  ): void {
    ctx.patchState({ byTable: without(ctx.getState().byTable, tableId) });
  }
}
